// A SAK manda o artigo inteiro (HTML) no campo `descricao` de cada artigo, com os vídeos
// embutidos em <iframe>: YouTube e Tella. Daqui sai o vídeo mostrado na resposta.

export type PlataformaVideo = "youtube" | "tella";

export interface VideoEncontrado {
  plataforma: PlataformaVideo;
  id: string;
  titulo: string;
}

const PADROES: { plataforma: PlataformaVideo; regex: RegExp }[] = [
  {
    plataforma: "youtube",
    regex:
      /(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:[^"'\s<>]*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/g,
  },
  { plataforma: "tella", regex: /tella\.tv\/video\/([A-Za-z0-9_-]+)/g },
];

// Vídeos na ordem em que aparecem no texto.
export const videosNoTexto = (texto?: string | null): Omit<VideoEncontrado, "titulo">[] => {
  if (!texto) return [];
  return PADROES.flatMap(({ plataforma, regex }) =>
    Array.from(texto.matchAll(regex), (m) => ({ plataforma, id: m[1], posicao: m.index ?? 0 }))
  )
    .sort((a, b) => a.posicao - b.posicao)
    .map(({ plataforma, id }) => ({ plataforma, id }));
};

export interface ArtigoComVideo {
  nomeartigo: string;
  link: string;
  descricao?: string;
  // Link do vídeo guardado na conversa (o HTML do artigo não é mais guardado).
  video?: string;
  slug_artigo?: string;
  "link-embed"?: string;
}

// "https://dropi.sak.com.br/faq/como-x/" e "dropi.sak.com.br/como-x" viram "dropi.sak.com.br/como-x".
const normalizarLink = (link: string) =>
  link
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/faq\//, "/")
    .replace(/[?#].*$/, "")
    .replace(/\/+$/, "");

const linksDaResposta = (resposta: string) =>
  Array.from(resposta.matchAll(/https?:\/\/[^\s"'<>)]+/g), (m) => normalizarLink(m[0]));

const videoDoArtigo = (artigo: ArtigoComVideo): VideoEncontrado | null => {
  const [video] = [...videosNoTexto(artigo.video), ...videosNoTexto(artigo.descricao)];
  return video ? { ...video, titulo: artigo.nomeartigo } : null;
};

// Qual vídeo mostrar:
// 1. um vídeo citado no próprio texto da resposta;
// 2. se a resposta cita um artigo, o vídeo DESSE artigo, e nenhum outro. A ordem dos
//    artigos da SAK não é confiável (o campo `relevancia` vem 0), e pegar o vídeo do
//    primeiro artigo da lista mostrava vídeos de outro assunto;
// 3. sem artigo citado, só o vídeo do primeiro artigo.
export const primeiroVideo = (
  resposta: string,
  artigos: ArtigoComVideo[] = [],
  // Link do artigo em que a IA da central disse ter se baseado: manda sobre os outros critérios.
  artigoUsado?: string | null
): VideoEncontrado | null => {
  const [noTexto] = videosNoTexto(resposta);
  if (noTexto) return { ...noTexto, titulo: "" };

  const citados = artigoUsado ? [normalizarLink(artigoUsado)] : linksDaResposta(resposta);
  if (citados.length) {
    const artigo = artigos.find((a) =>
      [a.link, a["link-embed"]].some((l) => l && citados.includes(normalizarLink(l)))
    );
    return artigo ? videoDoArtigo(artigo) : null;
  }

  return artigos[0] ? videoDoArtigo(artigos[0]) : null;
};
